import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { aiApi } from '../../utils/aiApi';

interface AIAnalyticsState {
  dashboardStats: {
    activeAssistants: number;
    totalConversations: number;
    messagesToday: number;
    successRate: number;
  };
  conversationTrends: {
    labels: string[];
    data: number[];
  };
  channelPerformance: {
    labels: string[];
    data: number[];
  };
  loading: boolean;
  error: string | null;
}

const initialState: AIAnalyticsState = {
  dashboardStats: {
    activeAssistants: 0,
    totalConversations: 0,
    messagesToday: 0,
    successRate: 0,
  },
  conversationTrends: {
    labels: [],
    data: [],
  },
  channelPerformance: {
    labels: [],
    data: [],
  },
  loading: false,
  error: null,
};

export const fetchAIDashboardStats = createAsyncThunk(
  'aiAnalytics/fetchDashboardStats',
  async () => {
    const response = await aiApi.get('/analytics/dashboard-stats');
    return response.data;
  }
);

export const fetchAIConversationTrends = createAsyncThunk(
  'aiAnalytics/fetchConversationTrends',
  async (period: string) => {
    const response = await aiApi.get(`/analytics/conversation-trends?period=${period}`);
    return response.data;
  }
);

export const fetchAIChannelPerformance = createAsyncThunk(
  'aiAnalytics/fetchChannelPerformance',
  async () => {
    const response = await aiApi.get('/analytics/channel-performance');
    return response.data;
  }
);

const aiAnalyticsSlice = createSlice({
  name: 'aiAnalytics',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAIDashboardStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAIDashboardStats.fulfilled, (state, action) => {
        state.loading = false;
        state.dashboardStats = action.payload;
      })
      .addCase(fetchAIDashboardStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch dashboard stats';
      })
      .addCase(fetchAIConversationTrends.fulfilled, (state, action) => {
        state.conversationTrends = action.payload;
      })
      .addCase(fetchAIChannelPerformance.fulfilled, (state, action) => {
        state.channelPerformance = action.payload;
      });
  },
});

export const { clearError } = aiAnalyticsSlice.actions;
export default aiAnalyticsSlice.reducer;